import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommentService } from "../../../services/notification/comment.service";
import {
  getAuditLogActionText,
  notificationMock,
} from "../../../mock/notification.mock";
import { NotificationService } from "../../../services/notification/notification.service";
import { StateService } from "../../../services/state.service";
import { MessageService } from "primeng/api";
import {
  formatDateTime,
  formatPatientAgeFronDoB,
} from "../../../util/common.util";
import { TEXTS } from "../../../constants/common.constants";
import { NotificationSharedService } from "../../notifications-shared.service";
import { catchError, forkJoin, of, Subscription } from "rxjs";
import { NgForm } from "@angular/forms";

@Component({
  selector: "mxn-notification-detail",
  templateUrl: "./notification-detail.component.html",
  styleUrl: "./notification-detail.component.scss",
})
export class NotificationDetailComponent implements OnInit {
  @Input() notification: any = {};
  @Input() checkboxSelectedNotifications: string[] = [];
  @Input() selectAllNotificationsInbox: boolean = false;
  @Input() currentPageSettings = {};
  @Input() totalNumberOfNotifications: number = 0;
  @Output() statusBadgeChangeEvent = new EventEmitter<{
    notificationId: string;
    status: string;
    previousStatus: string;
  }>();
  @Output() statusBadgeChangeEventForMultiple = new EventEmitter<{
    notificationIds: string[];
    status: string;
  }>();
  @Output() statusBadgeChangeEventForAll = new EventEmitter<{
    status: string;
  }>();
  @Output() markReadUnreadChangeEventForMultiple = new EventEmitter<{
    notificationIds: string[];
    markReadUnread: string;
  }>();
  @Output() markReadUnreadChangeEventForAll = new EventEmitter<{
    markReadUnread: string;
  }>();
  @Output() removedChangeEventForMultiple = new EventEmitter<{
    notificationIds: string[];
    currentPageSettings: any;
  }>();
  @Output() removedChangeEventForAll = new EventEmitter<any>();
  @Output() addCommentInNotification = new EventEmitter<{
    notificationId: string;
    comment: {
      id: string;
      created_at: string;
      updated_at: string;
      heading: string;
      text: string;
      deleted_at: null;
      privateComment: boolean;
    };
  }>();
  @Output() deleteCommentInNotification = new EventEmitter<{
    notificationId: string;
  }>();
  @Output() clearSelectedNotifications = new EventEmitter<string[]>();
  @Output() selectAllNotificationsOnCurrentPage = new EventEmitter<any>();
  @Output() selectAllNotificationsInboxPages = new EventEmitter<any>();

  @ViewChild("commentForm") commentForm!: NgForm;
  @ViewChild("commentsSection") commentsSection!: ElementRef;
  constructor(
    private commentService: CommentService,
    private notificationService: NotificationService,
    private stateService: StateService,
    private messageService: MessageService,
    private notificationSharedService: NotificationSharedService
  ) {}

  isSavingNewComment: boolean = false;
  editingCommentId: string = "";
  loading: boolean = false;
  isEmpty: boolean = false;
  @Input() showDefaultPage: boolean = false;
  visible: boolean = false;
  commentIndex: number = -1;
  comment: any = null;
  removeNotificationModal: boolean = false;
  removeNotificationsModalText: string = "";
  notificationWord: string = "";
  notStarted = TEXTS.notStartedText;
  inProgress = TEXTS.inProgressText;
  completed = TEXTS.completedText;
  markAsRead = TEXTS.markAsReadText;
  markAsUnread = TEXTS.markAsUnreadText;
  remove = TEXTS.removeText;
  alert = TEXTS.alertText;
  cancel = TEXTS.cancelText;
  private multiSelectFlagUpdateSubscription: Subscription | undefined;
  private setDefaultValuesMultiSelectUpdateSubscription:
    | Subscription
    | undefined;
  privateComment: boolean = true;

  commentLoading: boolean = false;
  auditLogLoading: boolean = false;
  temporaryAuditLogs = [];
  temporaryNotification = [];

  // Custom Comment Pagination attributes
  totalComments = 120;
  commentsPerPage = 10;
  currentCommentPage = 1;

  // Custom Audit-Log Pagination attributes
  totalAuditLogs = 120;
  auditLogsPerPage = 10;
  currentAuditLogPage = 1;

  statusBadgeIconMap = new Map([
    ["completed", "/assets/check.svg"],
    ["in-progress", "/assets/inprogress.svg"],
    ["not-started", "/assets/hovered.svg"],
  ]);

  ngOnInit(): void {
    this.getNotificationDetails();
    if (this.notification?.id) {
      this.isEmpty = false;
    } else {
      this.isEmpty = true;
    }
    this.stateService.setSelectedNotification(
      notificationMock(this.notification, this.stateService.getUser())
    );
    this.notification = notificationMock(
      this.notification,
      this.stateService.getUser()
    );

    this.multiSelectFlagUpdateSubscription =
      this.notificationSharedService.selectAllFlagTriggerAction$.subscribe(
        (selectAllFlag) => {
          this.selectAllNotificationsInbox = selectAllFlag;
        }
      );

    this.setDefaultValuesMultiSelectUpdateSubscription =
      this.notificationSharedService.setDefaultValuesMultiSelectSubjectTriggerAction$.subscribe(
        (value: any) => {
          this.checkboxSelectedNotifications = value?.selectedNotifications;
          this.selectAllNotificationsInbox =
            value?.selectAllNotificationsForMultiSelectFlag;
          this.showDefaultPage = value?.setDefaultPage;
        }
      );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["notification"] && !changes["notification"].isFirstChange()) {
      this.getNotificationDetails();
      this.newComment = "";
      this.privateComment = false;
      if (this.notification?.id) {
        this.isEmpty = false;
      } else {
        this.isEmpty = true;
      }
    }
    this.notificationWord =
      this.checkboxSelectedNotifications.length <= 1
        ? TEXTS.notificationText
        : TEXTS.notificationsText;
  }

  getNotificationDetails() {
    if (this.notification?.id) {
      this.loading = true;

      forkJoin({
        notificationResponse: this.notificationService
          .getNotificationById(this.notification?.id)
          .pipe(
            catchError((error) => {
              console.error("Error fetching notification:", error);
              return of(null);
            })
          ),
        commentsResponse: this.notificationService
          .getCommentsByNotification(
            this.notification?.id,
            this.currentCommentPage,
            this.commentsPerPage
          )
          .pipe(
            catchError((error) => {
              console.error("Error fetching comments:", error);
              return of(null);
            })
          ),
        auditLogsResponse: this.notificationService
          .getAuditLogsByNotification(
            this.notification?.id,
            this.currentAuditLogPage,
            this.auditLogsPerPage
          )
          .pipe(
            catchError((error) => {
              console.error("Error fetching audit_logs:", error);
              return of(null);
            })
          ),
      }).subscribe({
        next: ({
          notificationResponse,
          commentsResponse,
          auditLogsResponse,
        }: any) => {
          notificationResponse.comments = commentsResponse.comments;
          this.totalComments = commentsResponse.totalComments;

          notificationResponse.auditLogs = auditLogsResponse.auditLogs;
          this.totalAuditLogs = auditLogsResponse.totalAuditLogs;
          this.temporaryAuditLogs = auditLogsResponse.auditLogs;
          this.temporaryNotification = notificationResponse;

          this.stateService.setSelectedNotification(notificationResponse);
          this.notification = notificationMock(
            {
              ...this.stateService.getSelectedNotification(),
              id: this.notification?.id,
              status: notificationResponse.status || this.notification?.status,
            },
            this.stateService.getUser()
          );
          this.loading = false;
          // Send postMessage to parent
          window.parent.postMessage(
            {
              type: "UUX-Patient-Context",
              patient: [
                {
                  patientMrn:
                    notificationResponse.encounter.source_patient_identifier,
                  patientOrganization:
                    notificationResponse.encounter.source_uid,
                },
              ],
            },
            "*"
          );
        },
        error: (err) => {
          this.isEmpty = true;
          this.loading = false;
        },
      });
    }
  }
  getAllCommentsCall() {
    if (this.notification?.id) {
      this.commentService
        .getCommentByNotificationId(this.notification?.id)
        .subscribe({
          next: (res) => {
            if (res.status === 200) {
              this.notification.comments = res.data;
            } else {
              this.notification.comments = [];
            }
          },
          error: (err) => {},
        });
    }
  }
  getFormatedAuditLogData() {
    if (
      !this.notification?.auditLogs ||
      this.notification?.auditLogs.length === 0
    ) {
      return [];
    }
    return this.notification.auditLogs.map((log: any) => {
      return {
        dateTime: log.created_at,
        performedBy:
          log.user.first_name || log.user.last_name
            ? `${log.user.first_name} ${log.user.last_name}`
            : `${log.user.email}`,
        action: log.type,
      };
    });
  }
  demographicsColumns: { [key: string]: string } = {
    dateTime: "Date - Time",
    performedBy: "Performed By",
    action: "Action",
  };
  diagnosisColumns: { [key: string]: string } = {
    name: "Type",
    category: "Code",
    quantity: "Description",
  };
  demographicsHeaderClasses: string[] = [
    "w-15rem py-2 pr-2 pl-4",
    "w-15rem py-2 px-2",
    "px-2 py-2",
  ];
  demographicsBodyClasses: string[] = [
    "p-2 pl-4 mxn-td-label",
    "p-2 mxn-td-label",
    "p-2 mxn-td-info",
  ];
  diagnosisHeaderClasses: string[] = [
    "w-4rem py-2 px-2",
    "w-5rem py-2 px-2",
    "px-2 py-2",
  ];
  diagnosisBodyClasses: string[] = [
    "py-1 px-2 mxn-td-label",
    "py-1 px-2 mxn-td-label",
    "py-1 px-2 mxn-td-info",
  ];
  laboratoryColumns: { [key: string]: string } = {
    test: "Test",
    //observationDate: "Observation Date",
    lastResultDate: "Result Date",
    source: "Performed At",
    sourceMRN: "Source MRN",
    status: "Status",
  };

  laboratoryNestedColumns: { [key: string]: string } = {
    item: "Test Item",
    date: "Test Item Date",
    resultValue: "Result Value",
    unit: "Unit",
    referenceRange: "Reference Range",
    messageFlag: "Message Flag",
    status: "Status",
  };

  labHeaderClasses: string[] = [
    "w-3 py-2 pr-2 pl-4",
    "w-3 py-2 px-2",
    "w-3 py-2 px-2",
    "w-3 py-2 px-2",
    "w-10rem px-2 py-2 white-space-nowrap",
  ];

  labBodyClasses: string[] = [
    "p-2 pl-4 mxn-td-info",
    "p-2 mxn-td-info",
    "p-2 mxn-td-info",
    "p-2 mxn-td-info",
    "p-2 mxn-td-info",
  ];
  nestedLabHeaderClasses: string[] = [
    "w-13rem py-2 pr-2 pl-4 mxn-td-nested-label",
    "w-13rem py-2 px-2 mxn-td-nested-label",
    "w-13rem py-2 px-2 mxn-td-nested-label",
    "w-13rem py-2 px-2 mxn-td-nested-label",
    "px-2 py-2 mxn-td-nested-label",
    "mxn-td-nested-label",
    "mxn-td-nested-label",
  ];

  nestedLabBodyClasses: string[] = [
    "p-2 pl-4 mxn-td-nested-info",
    "p-2 mxn-td-nested-info",
    "p-2 mxn-td-nested-info",
    "p-2 mxn-td-nested-info",
    "p-2 mxn-td-nested-info",
    "p-2 mxn-td-nested-info",
    "p-2 mxn-td-nested-info",
  ];

  newComment = "";

  onEditComment(comnt: any) {
    const commentToEdit = this.notification.comments.find(
      (comment: { id: string }) => comment.id === comnt.id
    );
    if (commentToEdit) {
      commentToEdit.text = comnt.text;
      this.editingCommentId = "";
    }
  }

  toggleEditComment(comnt: any) {
    const commentToEdit = this.notification.comments.find(
      (comment: { id: string }) => comment.id === comnt.id
    );
    if (commentToEdit) {
      this.editingCommentId = commentToEdit.id;
    }
  }

  onDeleteComment(comment: any) {
    this.commentIndex = this.notification.comments.findIndex(
      (c: { id: string }) => c.id === comment.id
    );
    this.comment = comment;
    if (this.commentIndex !== -1) {
      this.comment = comment;
      this.visible = true;
    }
  }

  onDeleteCommentConfirm() {
    this.notification.comments.splice(this.commentIndex, 1);
    this.commentService
      .deleteComment(this.comment?.id, this.comment?.privateComment)
      .subscribe({
        next: (res) => {
          this.getNotificationDetails();
          if (this.notification.comments.length === 0) {
            this.deleteCommentInNotification.emit({
              notificationId: this.notification.id,
            });
          }
        },
        error: (err) => {},
      });
    this.commentIndex = -1;
    this.comment = null;
  }

  handleAddComment() {
    if (this.newComment.trim() === "") {
      alert("Comment cannot be empty");
      return;
    }
    this.isSavingNewComment = true;
    this.commentService
      .createComment({
        notification_id: this.notification.id,
        heading: "heading",
        text: this.newComment,
        privateComment: this.privateComment,
      })
      .subscribe({
        next: (res) => {
          this.getNotificationDetails();
          this.addCommentInNotification.emit({
            notificationId: this.notification.id,
            comment: {
              id: "-",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
              heading: "heading",
              text: this.newComment,
              deleted_at: null,
              privateComment: this.privateComment,
            },
          });
          this.newComment = "";
          this.isSavingNewComment = false;
          this.editingCommentId = "";
          this.privateComment = false;
        },
        error: (err) => {
          this.isSavingNewComment = false;
        },
      });
  }

  scrollToComments() {
    if (this.commentsSection) {
      this.commentsSection.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  onStatusChange(notificationId: string, status: string) {
    let previousStatus = this.notification.statusBadge;
    this.notificationService
      .updateNotificationStatus(notificationId, {
        newStatus: status,
        currentStatus: previousStatus,
      })
      .subscribe({
        next: (res) => {
          this.notification.statusBadge = status;
          this.statusBadgeChangeEvent.emit({
            notificationId,
            status,
            previousStatus,
          });
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: "Status updated successfully",
          });
          this.getNotificationDetails();
        },
        error: (err) => {
          console.error("Error while subscribing:", err);
        },
      });
  }
  formatPatientAgeFronDoB(dob: string): string {
    return formatPatientAgeFronDoB(dob);
  }

  clearMultiSelectedNotifications() {
    this.selectAllNotificationsInbox = false;
    this.checkboxSelectedNotifications = [];
    this.clearSelectedNotifications.emit(this.checkboxSelectedNotifications);
  }

  selectAllNotificationsOnPage() {
    this.selectAllNotificationsInbox = false;
    this.selectAllNotificationsOnCurrentPage.emit();
  }

  selectAllNotificationsInInbox() {
    this.selectAllNotificationsInbox = true;
    this.selectAllNotificationsInboxPages.emit();
  }

  updateNotificationsStatus(status: string) {
    if (this.checkboxSelectedNotifications.length == 0) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: TEXTS.errorMessageNoNotificationSelected,
      });

      return;
    }

    this.updateNotifications(status);
  }

  makeNotificationsReadUnread(markAsReadUnread: string) {
    if (this.checkboxSelectedNotifications.length == 0) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: TEXTS.errorMessageNoNotificationSelected,
      });

      return;
    }

    this.updateNotifications("", markAsReadUnread);
  }

  removeNotifications() {
    if (this.checkboxSelectedNotifications.length == 0) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: TEXTS.errorMessageNoNotificationSelected,
      });

      return;
    }

    this.removeNotificationsModalText = this.selectAllNotificationsInbox
      ? TEXTS.selectAllNotificationRemoveModalText
      : TEXTS.selectSomeNotificationRemoveModalText(
          this.checkboxSelectedNotifications.length,
          this.notificationWord
        );

    this.removeNotificationModal = true;
  }

  onRemoveNotificationsConfirm() {
    this.updateNotifications("", "", true);
  }

  private updateNotifications(
    status: string = "",
    markReadUnread: string = "",
    remove: boolean = false
  ) {
    const body = Object.assign(
      {
        newStatus: status,
        notificationIds: this.selectAllNotificationsInbox
          ? []
          : this.checkboxSelectedNotifications,
        allNotifications: this.selectAllNotificationsInbox,
        updateNotificationReadUnread: markReadUnread,
        remove: remove,
      },
      this.currentPageSettings
    );

    this.notificationService.updateNotifications(body).subscribe({
      next: (res) => {
        if (!(markReadUnread == "true" || markReadUnread == "false")) {
          if (!this.selectAllNotificationsInbox) {
            this.statusBadgeChangeEventForMultiple.emit({
              notificationIds: this.checkboxSelectedNotifications,
              status,
            });
          } else {
            this.statusBadgeChangeEventForAll.emit({
              status,
            });
          }
        }

        if (markReadUnread == "true" || markReadUnread == "false") {
          if (!this.selectAllNotificationsInbox) {
            this.markReadUnreadChangeEventForMultiple.emit({
              notificationIds: this.checkboxSelectedNotifications,
              markReadUnread,
            });
          } else {
            this.markReadUnreadChangeEventForAll.emit({
              markReadUnread,
            });
          }
        }

        if (remove) {
          if (this.selectAllNotificationsInbox) {
            this.removedChangeEventForAll.emit();
          } else {
            this.removedChangeEventForMultiple.emit({
              notificationIds: this.checkboxSelectedNotifications,
              currentPageSettings: this.currentPageSettings,
            });
          }
        }

        this.messageService.add({
          severity: "success",
          summary: "Operation Successful",
          detail: res.message,
        });
      },
      error: (err) => {},
    });
  }

  resetNotificationDetailInteractivity() {
    this.isEmpty = true;
  }

  ngOnDestroy() {
    this.multiSelectFlagUpdateSubscription?.unsubscribe();
    this.setDefaultValuesMultiSelectUpdateSubscription?.unsubscribe();
  }

  onCommentsPageChange(event: any) {
    this.commentLoading = true;
    this.currentCommentPage = event.currentPage;
    this.commentsPerPage = event.rowsPerPage;

    this.notificationService
      .getCommentsByNotification(
        this.notification?.id,
        this.currentCommentPage,
        this.commentsPerPage
      )
      .subscribe({
        next: (commentsResponse: any) => {
          this.notification = this.temporaryNotification;
          this.notification.comments = commentsResponse.comments;
          this.notification.auditLogs = this.temporaryAuditLogs;
          this.stateService.setSelectedNotification(this.notification);
          this.notification = notificationMock(
            {
              ...this.stateService.getSelectedNotification(),
              id: this.notification?.id,
              status: this.notification?.status,
            },
            this.stateService.getUser()
          );

          this.totalComments = commentsResponse.totalComments;
          this.commentLoading = false;
        },
        error: (err) => {
          this.commentLoading = false;
        },
      });
  }

  onAuditLogPageChange(event: any) {
    this.auditLogLoading = true;
    this.currentAuditLogPage = event.currentPage;
    this.auditLogsPerPage = event.rowsPerPage;

    this.notificationService
      .getAuditLogsByNotification(
        this.notification?.id,
        this.currentAuditLogPage,
        this.auditLogsPerPage
      )
      .subscribe({
        next: (auditLogsResponse: any) => {
          this.notification.auditLogs = this.setAuditLogForNotification(
            auditLogsResponse.auditLogs
          ).auditLogs;
          this.totalAuditLogs = auditLogsResponse.totalAuditLogs;
          this.auditLogLoading = false;
        },
        error: (err) => {
          this.auditLogLoading = false;
        },
      });
  }

  setAuditLogForNotification(auditLogs: any) {
    const updatedAuditLog = {
      auditLogs:
        auditLogs?.map((auditLog: any) => ({
          dateTime: formatDateTime(auditLog.created_at) || "-",
          performedBy:
            auditLog.user.first_name || auditLog.user.last_name
              ? `${auditLog.user.first_name} ${auditLog.user.last_name}`
              : `${auditLog.user.email ?? "-"}`,
          action: getAuditLogActionText(auditLog) || "-",
        })) || [],
    };

    return updatedAuditLog;
  }
}
